import React, { useState } from 'react';
import { Button, Form, Modal, Alert, Spinner } from 'react-bootstrap';

/**
 * Component Scraper UI
 * 
 * This component provides a user interface for triggering the component scraper
 * from within the application. It shows a modal with options for configuring
 * the scraping process.
 */
const ComponentScraper = () => {
  const [showModal, setShowModal] = useState(false);
  const [url, setUrl] = useState(window.location.href);
  const [isLoading, setIsLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  
  const handleClose = () => {
    setShowModal(false);
    // Reset state after modal closes
    setTimeout(() => {
      setResult(null);
      setError(null);
    }, 300);
  };
  
  const handleShow = () => setShowModal(true);
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setResult(null);
    setError(null);
    
    try {
      // Call the actual API endpoint
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3030';
      const response = await fetch(`${apiUrl}/api/scrape-components`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ url }),
      });
      
      const data = await response.json();
      
      if (!data.success) {
        throw new Error(data.message || 'Failed to scrape components');
      }
      
      setResult(data);
    } catch (err) {
      console.error('Error scraping components:', err);
      setError('Failed to scrape components. Please check the console for details.');
    } finally {
      setIsLoading(false);
    }
  };
  
  return (
    <>
      {/* Button to open the modal */}
      <Button 
        variant="outline-secondary" 
        size="sm" 
        onClick={handleShow}
        className="ms-2"
        title="ToDo: Scrape component selectors"
        disabled={true}
      >
        <i className="fa fa-code"></i> Scrape Components
      </Button>
      
      {/* Modal for component scraper configuration */}
      <Modal show={showModal} onHide={handleClose}>
        <Modal.Header closeButton>
          <Modal.Title>Component Selector Scraper</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {error && (
            <Alert variant="danger">
              {error}
            </Alert>
          )}
          
          {result ? (
            <Alert variant="success">
              <Alert.Heading>Scraping Completed!</Alert.Heading>
              <p>{result.message}</p>
              <hr />
              <p className="mb-0">
                Found {result.componentsFound} components and saved to:<br />
                <code>{result.exportPath}</code>
              </p>
            </Alert>
          ) : (
            <Form onSubmit={handleSubmit}>
              <Form.Group className="mb-3">
                <Form.Label>URL to scrape</Form.Label>
                <Form.Control 
                  type="text" 
                  value={url} 
                  onChange={(e) => setUrl(e.target.value)}
                  disabled={isLoading}
                  placeholder="Enter URL to scrape"
                />
                <Form.Text className="text-muted">
                  The page to analyze for component selectors
                </Form.Text>
              </Form.Group>
              
              <div className="d-grid gap-2">
                <Button 
                  variant="primary" 
                  type="submit"
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <>
                      <Spinner
                        as="span"
                        animation="border"
                        size="sm"
                        role="status"
                        aria-hidden="true"
                        className="me-2"
                      />
                      Scraping...
                    </>
                  ) : 'Start Scraping'}
                </Button>
              </div>
            </Form>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleClose}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default ComponentScraper; 